/**
 * Shared helpers for numbering and searching the lessons of a Scheme of Work (SoW).
 * The SoW screen and the lesson plan modal both use these so they always show the same lesson numbers.
 */

// Lesson numbers shown on the SoW screen for the rows of ONE week, in order.
// A row can cover several periods (its `lessons` value, e.g. "1-2"), so numbering continues after them.
export const computeLessonDisplayData = (lessons) => {
    let currentLessonNumber = 1;
    return lessons.map(lesson => {
        let count = 1;
        if (lesson.lessons) {
            count = String(lesson.lessons).split(/[- ,]+/).filter(Boolean).length;
        } else if (lesson.lesson || lesson.lesson_number) {
            count = 1; // Explicit JSON fallback for single periods
        }
        count = Math.max(1, count); // Ensure at least 1 period

        let displayParts = [];
        for (let i = 0; i < count; i++) {
            displayParts.push(String(currentLessonNumber + i));
        }
        currentLessonNumber += count;

        return displayParts;
    });
};

export const rowWeek = (row) => row.week_number || row.week || 1;

/**
 * Every lesson of the SoW as a flat list, with the lesson number it has on the SoW screen.
 * Rows are grouped by week in their original order (the same grouping the SoW screen uses) and numbered
 * over the whole week, not just over rows of one sub-strand.
 * Each option: { row, week, lessonNumbers: ['2'], lessonLabel: 'Lesson 2', title }
 */
export const buildSowLessonOptions = (weeklyBreakdown) => {
    const rows = Array.isArray(weeklyBreakdown) ? weeklyBreakdown : [];
    const byWeek = {};
    rows.forEach(row => {
        const week = rowWeek(row);
        if (!byWeek[week]) byWeek[week] = [];
        byWeek[week].push(row);
    });

    const options = [];
    Object.keys(byWeek)
        .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
        .forEach(week => {
            const weekRows = byWeek[week];
            const display = computeLessonDisplayData(weekRows);
            weekRows.forEach((row, i) => {
                options.push({
                    row,
                    week: Number(week),
                    lessonNumbers: display[i],
                    lessonLabel: display[i].map(n => `Lesson ${n}`).join(', '),
                    title: String(row.lesson_title || row.title || '').trim()
                });
            });
        });
    return options;
};

// Keeps the options matching `matchesSubStrand(row)` (when given) and whose title contains `query`.
export const filterSowLessonOptions = (options, { matchesSubStrand = null, query = '' } = {}) => {
    const q = String(query || '').trim().toLowerCase();
    return options.filter(option => {
        if (matchesSubStrand && !matchesSubStrand(option.row)) return false;
        if (q && !option.title.toLowerCase().includes(q)) return false;
        return true;
    });
};
